import { Client, IMessage } from '@stomp/stompjs';

import storage, { EStorageKey } from '../../storage';
import { formatTopicId, formatTopicPath } from '../utils';

export enum EChartTopicPeriod {
  MINUTE_1 = '1m',
  MINUTE_2 = '2m',
  MINUTE_3 = '3m',
  MINUTE_4 = '4m',
  MINUTE_5 = '5m',
  MINUTE_6 = '6m',
  MINUTE_10 = '10m',
  MINUTE_15 = '15m',
  MINUTE_20 = '20m',
  MINUTE_30 = '30m',
  HOUR_1 = '1h',
  HOUR_2 = '2h',
  HOUR_3 = '3h',
  HOUR_4 = '4h',
  HOUR_6 = '6h',
  HOUR_8 = '8h',
  HOUR_12 = '12h',
  DAY = '1d',
  WEEK = '1w',
  MONTH = '1mth'
}

export enum SYMBOL_MESSAGE_TYPE {
  LIVE = 'LIVE'
}

export enum CHART_MESSAGE_TYPE {
  MOVE = 'MOVE',
  PATCH = 'PATCH'
}

export interface IChartTopicPatchData {
  T: number; // time
  C: number; // close
  H: number; // high
  L: number; // low
  O: number; // open
  A: number; // ask
}

export interface IChartTopicMessage {
  M: CHART_MESSAGE_TYPE;
  D: IChartTopicPatchData;
}

export interface IChartTopicReceiver {
  subscriptionKey: string;
  payload: (payload: IChartTopicMessage | undefined) => void;
}

export interface ISubscribeParams {
  symbol: string;
  period: EChartTopicPeriod;
  receiver: IChartTopicReceiver;
}

interface IProps {
  client: Client;
}

class ChartTopic {
  private client: Client;
  private receivers: Record<string, Set<IChartTopicReceiver> | undefined> = {};

  constructor(props: IProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (params: ISubscribeParams) => {
    const { period, symbol, receiver } = params;

    const { subPath, topicId } = this.mapPeriodToIds(period, symbol);

    if (!this.client) {
      return;
    }
    if (this.receivers[subPath]) {
      let isKeyExisting = false;
      this.receivers[subPath]?.forEach((r) => {
        isKeyExisting = r.subscriptionKey === receiver.subscriptionKey;
      });

      if (!isKeyExisting) {
        this.receivers[subPath]?.add(receiver);
      }
    } else {
      this.receivers[subPath] = new Set([receiver]);
      try {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: topicId
        });
      } catch (err) {}
    }
  };

  public unsubscribe = (params: ISubscribeParams) => {
    const { period, symbol, receiver } = params;
    const { subPath, topicId } = this.mapPeriodToIds(period, symbol);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]?.size !== 0) {
      let lastItem = null;
      this.receivers[subPath]?.forEach((r) => {
        if (r.subscriptionKey === receiver.subscriptionKey) {
          lastItem = r;
        }
      });

      if (lastItem) {
        this.receivers[subPath]?.delete(lastItem);
      }
    }
    if (this.receivers[subPath]?.size === 0) {
      try {
        this.client?.unsubscribe(topicId);
      } catch (err) {}

      this.receivers[subPath] = undefined;
      delete this.receivers[subPath];
    }
  };

  public clearSubscriptions = () => {
    this.receivers = {};
  };

  public unsubscribeAll = () => {
    Object.keys(this.receivers).forEach((subPath) => {
      try {
        this.client?.unsubscribe(formatTopicId(subPath));
      } catch (err) {}
    });
    this.receivers = {};
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  private onReceive = (id: string, message: IMessage) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body);

    receivers.forEach((receiver) => receiver.payload(payload));
  };

  private mapPeriodToIds = (period: EChartTopicPeriod, symbol: string) => {
    const marketSubscription = storage.getItem(EStorageKey.MARKET_SUBSCRIPTION);
    const subPath = `chart${period}/${marketSubscription}/${symbol}`;

    return {
      subPath,
      topicId: formatTopicId(subPath)
    };
  };
}

export default ChartTopic;
